import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wallet, TrendingUp, Users, ArrowUpRight, DollarSign, CalendarDays, ShieldCheck, Send, TrendingDown, Zap, Repeat } from "lucide-react";
import { api } from "../../lib/api";
import FounderBankCard from "./FounderBankCard";
import BankReconciliationCard from "./BankReconciliationCard";
import PayStaffModal from "./PayStaffModal";

/**
 * Founder-only "how much have we actually made" console. Reads
 * /admin-auth/founders-bank which aggregates every paid Stripe transaction
 * and renders:
 *   - lifetime balance + 30-day + 7-day rollups
 *   - 12-month revenue bar chart
 *   - top paying clients leaderboard
 *   - by-tier breakdown (which packages sell)
 *   - recent transactions table
 */
export default function FoundersBank() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [showPayModal, setShowPayModal] = useState(false);
  const [myBank, setMyBank] = useState(null);
  const [balances, setBalances] = useState([]);   // live Stripe FC balances

  const load = () => {
    api.get("/admin-auth/founders-bank")
      .then(({ data }) => { setData(data); setState("ok"); })
      .catch((e) => {
        if (e?.response?.status === 403) setState("denied");
        else { setState("error"); toast.error("Could not load revenue data."); }
      });
  };

  const refreshBankMeta = () => {
    api.get("/admin-auth/founder-banks")
      .then(({ data }) => {
        const list = data.founders || [];
        // Pick primary bank from self, else any bank.
        const selfWithBank = list.find((f) => f.is_self && (f.banks || []).length > 0);
        const anyWithBank  = list.find((f) => (f.banks || []).length > 0);
        const chosen = selfWithBank || anyWithBank;
        if (chosen) {
          const primary = (chosen.banks || []).find((b) => b.is_primary) || chosen.banks[0];
          setMyBank({
            has_bank: true,
            is_self: chosen.is_self,
            username: chosen.username,
            bank_name: primary?.bank_name,
            bank_last4: primary?.bank_last4,
            bank_account_type: primary?.bank_account_type,
          });
        } else setMyBank(null);
      })
      .catch(() => {});
    api.get("/admin-auth/founder-banks/balances")
      .then(({ data }) => {
        // Flatten to only linked-bank entries with a balance number.
        const flat = [];
        for (const f of (data.balances || [])) {
          for (const b of (f.banks || [])) {
            if (b.linked) {
              flat.push({
                founder: f.display_name || f.username,
                bank_name: b.fc_bank_name || b.bank_name,
                bank_last4: b.fc_bank_last4 || b.bank_last4,
                balance_cents: (b.balance && !b.balance.error) ? (b.balance.available_cents ?? b.balance.current_cents ?? null) : null,
                error: b.balance?.error,
              });
            }
          }
        }
        setBalances(flat);
      })
      .catch(() => {});
  };

  useEffect(() => {
    load();
    refreshBankMeta();
  }, []);

  const monthlyMax = useMemo(() => {
    if (!data?.monthly?.length) return 0;
    return Math.max(...data.monthly.map((m) => m.total));
  }, [data]);

  if (state === "loading") {
    return (
      <div data-testid="founders-bank-loading" className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-[#c9a961]" />
      </div>
    );
  }
  if (state === "denied") {
    return (
      <div data-testid="founders-bank-denied" className="rounded-2xl border border-[#b23a48]/30 bg-[#b23a48]/8 p-6 text-center">
        <ShieldCheck className="h-6 w-6 mx-auto text-[#b23a48]" />
        <p className="mt-2 text-sm text-[#b23a48]">Founder access required. Only Kalib and John can view revenue.</p>
      </div>
    );
  }
  if (state !== "ok" || !data) return null;

  const b = data.balance;
  const c = data.counters;

  return (
    <div data-testid="tab-content-founders-bank" className="space-y-6">
      {/* Hero — the balance card */}
      <div className="rounded-3xl bg-gradient-to-br from-[#1e3d3a] via-[#0f2624] to-[#1e3d3a] text-white p-6 lg:p-8 shadow-xl relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#c9a961]/10 blur-3xl" />
        <div className="absolute -left-10 -bottom-10 w-52 h-52 rounded-full bg-[#c9a961]/5 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-[#c9a961]" />
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#c9a961]">Elevare · Founders bank</p>
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-[0.26em] text-white/45">Lifetime revenue collected</p>
          <p
            data-testid="founders-bank-lifetime"
            className="font-head text-5xl md:text-6xl mt-2 tabular-nums text-white"
          >
            {money(b.lifetime_total)}
          </p>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <HeroStat label="Last 30 days" value={money(b.mrr_30d)} accent="gold" testid="fb-mrr-30d" />
            <HeroStat label="This week"    value={money(b.weekly)} testid="fb-weekly" />
            <HeroStat label="Paying clients" value={c.paying_clients} testid="fb-paying-clients" />
            <HeroStat label="Avg / client"  value={money(b.avg_per_client)} testid="fb-avg" />
          </div>

          {balances.length > 0 && (
            <div className="mt-5 rounded-2xl bg-white/10 border border-white/15 p-4" data-testid="fb-live-balances">
              <p className="text-[9px] uppercase tracking-[0.28em] text-white/50 mb-2">Live bank balances</p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {balances.map((b, i) => (
                  <li key={i} data-testid={`fb-live-balance-${i}`} className="flex items-baseline justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[13px] text-white truncate">{b.bank_name} · ••••{b.bank_last4}</p>
                      <p className="text-[9px] uppercase tracking-[0.2em] text-white/45">{b.founder}</p>
                    </div>
                    <span className="text-2xl font-head tabular-nums text-[#c9a961]">
                      {b.error ? "—" : (b.balance_cents != null ? money(b.balance_cents / 100) : "…")}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-[9px] uppercase tracking-[0.22em] text-white/35 mt-2">
                Refreshed via Stripe Financial Connections · updates on load
              </p>
            </div>
          )}

          <div className="mt-5 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-4 text-[10px] uppercase tracking-[0.24em] text-white/55">
              <span>{c.paid_transactions} paid transactions</span>
              <span>·</span>
              <span>{c.active_hires_on_payroll} on payroll</span>
              <span>·</span>
              <span>{c.recurring_authorized || 0} on recurring ACH</span>
              {c.engaged_only ? (<><span>·</span><span>{c.engaged_only} engaged (not billed yet)</span></>) : null}
            </div>
            <button
              type="button"
              data-testid="founders-bank-pay-staff"
              onClick={() => setShowPayModal(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[#c9a961] text-[#1e3d3a] px-5 py-2 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-white transition-colors duration-300"
            >
              <Send className="h-3.5 w-3.5" />
              Pay staff
            </button>
          </div>
        </div>
      </div>

      {/* Monthly bar chart */}
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[#a3823f]" />
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Monthly revenue · trailing 12</p>
          </div>
        </div>
        <div className="flex items-end justify-between gap-1.5 h-40" data-testid="fb-monthly-chart">
          {data.monthly.map((m) => {
            const pct = monthlyMax ? (m.total / monthlyMax) * 100 : 0;
            const monthLabel = new Date(`${m.month}-01T12:00:00Z`).toLocaleDateString(undefined, { month: "short" });
            return (
              <div key={m.month} className="flex-1 flex flex-col items-center justify-end min-w-0" title={`${m.month}: ${money(m.total)}`}>
                <span className="text-[9px] tabular-nums text-[#1e3d3a]/50 mb-1">
                  {m.total > 0 ? shortMoney(m.total) : ""}
                </span>
                <div
                  className={`w-full max-w-[42px] rounded-t transition-all duration-500 ${
                    m.total > 0 ? "bg-gradient-to-t from-[#c9a961] to-[#a3823f]" : "bg-[#1e3d3a]/6"
                  }`}
                  style={{ height: `${Math.max(3, pct)}%` }}
                />
                <span className="text-[9px] uppercase tracking-[0.14em] text-[#1e3d3a]/45 mt-1">
                  {monthLabel}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top paying clients */}
        <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-[#a3823f]" />
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Top paying clients</p>
          </div>
          {(data.top_clients || []).length === 0 ? (
            <p className="text-[13px] text-[#1e3d3a]/45 py-6 text-center">No paid transactions yet.</p>
          ) : (
            <ol className="space-y-1" data-testid="fb-top-clients">
              {data.top_clients.map((c, i) => (
                <li key={c.client_id} data-testid={`fb-client-${c.client_id}`} className="flex items-center justify-between gap-3 py-2 border-b border-[#1e3d3a]/6 last:border-b-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-6 h-6 rounded-full bg-[#c9a961]/15 text-[#a3823f] text-[10px] font-bold flex items-center justify-center tabular-nums shrink-0">
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[13px] text-[#1e3d3a] truncate">
                        {c.business_name || c.client_id}
                      </p>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/45 truncate">
                        {c.count} txn{c.count === 1 ? "" : "s"}{c.owner_username ? ` · @${c.owner_username}` : ""}
                      </p>
                    </div>
                  </div>
                  <span className="text-[13px] tabular-nums font-medium text-[#a3823f] shrink-0">
                    {money(c.total)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* By package tier */}
        <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="h-4 w-4 text-[#a3823f]" />
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Revenue by tier</p>
          </div>
          {(data.by_tier || []).length === 0 ? (
            <p className="text-[13px] text-[#1e3d3a]/45 py-6 text-center">No sales yet.</p>
          ) : (
            <ul className="space-y-2" data-testid="fb-by-tier">
              {data.by_tier.map((t) => {
                const share = b.lifetime_total ? (t.total / b.lifetime_total) * 100 : 0;
                return (
                  <li key={t.lookup_key} data-testid={`fb-tier-${t.lookup_key}`}>
                    <div className="flex items-baseline justify-between text-[11px]">
                      <span className="text-[#1e3d3a] font-medium truncate">
                        {(t.lookup_key || "other").replace(/_/g, " ")}
                      </span>
                      <span className="tabular-nums text-[#a3823f]">
                        {money(t.total)} · {t.count} sale{t.count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-[#1e3d3a]/6 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#c9a961] to-[#a3823f] transition-all duration-500"
                        style={{ width: `${share}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Recent transactions */}
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-[#1e3d3a]/8 flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-[#a3823f]" />
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Recent transactions</p>
        </div>
        {(data.recent || []).length === 0 ? (
          <p className="text-[13px] text-[#1e3d3a]/45 py-8 text-center">No payments recorded yet.</p>
        ) : (
          <table className="w-full text-[13px]" data-testid="fb-recent-txns">
            <thead className="bg-[#eae7de] text-[10px] uppercase tracking-[0.25em] text-[#1e3d3a]/60">
              <tr>
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Client</th>
                <th className="text-left px-4 py-2.5">Tier</th>
                <th className="text-right px-4 py-2.5">Amount</th>
                <th className="text-right px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((r) => (
                <tr key={r.session_id} data-testid={`fb-txn-${r.session_id}`} className="border-t border-[#1e3d3a]/6">
                  <td className="px-4 py-2 text-[#1e3d3a]/70 text-[11px] tabular-nums">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" }) : "—"}
                  </td>
                  <td className="px-4 py-2 text-[#1e3d3a] truncate max-w-[240px]">{r.email || "—"}</td>
                  <td className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/55">
                    {(r.lookup_key || "—").replace(/_/g, " ")}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium text-[#a3823f]">
                    {money(r.amount)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <ArrowUpRight className="h-3.5 w-3.5 text-[#1e3d3a]/30 inline" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Forecast + FounderBankCard + Modal */}
      {data.forecast && (
        <div className="rounded-2xl border border-[#c9a961]/40 bg-white p-5" data-testid="fb-forecast">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="h-4 w-4 text-[#a3823f]" />
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Next quarter forecast</p>
            <span className="text-[9px] uppercase tracking-[0.22em] text-[#1e3d3a]/40 truncate">
              · {data.forecast.next_quarter_months?.join(" · ")}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ForecastCard tone="quiet" label="Conservative" value={data.forecast.conservative} sub={`floor from ${c.recurring_authorized || 0} recurring ACH signers`} testid="fb-forecast-conservative" />
            <ForecastCard tone="gold"  label="Base"         value={data.forecast.base}         sub={`trailing 3-mo avg × 3`}                                        testid="fb-forecast-base" />
            <ForecastCard tone="teal"  label="Optimistic"   value={data.forecast.optimistic}   sub={`slope-adjusted (${data.forecast.slope_per_month >= 0 ? "+" : ""}${money(data.forecast.slope_per_month)}/mo)`} testid="fb-forecast-optimistic" />
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">
            <span>3-mo avg <span className="tabular-nums text-[#a3823f]">{money(data.forecast.trailing_3mo_avg)}</span></span>
            <span>6-mo avg <span className="tabular-nums text-[#a3823f]">{money(data.forecast.trailing_6mo_avg)}</span></span>
            <span>12-mo avg <span className="tabular-nums text-[#a3823f]">{money(data.forecast.trailing_12mo_avg)}</span></span>
            <span className="text-[#1e3d3a]/30">method: {data.forecast.method}</span>
          </div>
        </div>
      )}

      {(data.recurring_roster?.length > 0 || data.engaged_roster?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5" data-testid="fb-recurring-roster">
            <div className="flex items-center gap-2 mb-3">
              <Repeat className="h-4 w-4 text-[#a3823f]" />
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Active recurring ACH</p>
              <span className="text-[10px] uppercase tracking-[0.24em] text-[#1e3d3a]/40 ml-auto">
                {c.recurring_authorized || 0} authorized
              </span>
            </div>
            {(data.recurring_roster || []).length === 0 ? (
              <p className="text-[13px] text-[#1e3d3a]/45 py-6 text-center">No active recurring clients yet.</p>
            ) : (
              <ul className="space-y-1">
                {data.recurring_roster.map((r) => (
                  <li key={r.client_id} data-testid={`fb-recurring-${r.client_id}`} className="py-1.5 flex items-baseline justify-between gap-2 border-b border-[#1e3d3a]/6 last:border-b-0">
                    <div className="min-w-0">
                      <p className="text-[12px] text-[#1e3d3a] truncate">{r.business_name || r.client_id}</p>
                      <p className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45 truncate">{r.client_id}</p>
                    </div>
                    {r.stripe_total > 0 && <span className="text-[11px] tabular-nums text-[#a3823f] shrink-0">{money(r.stripe_total)}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5" data-testid="fb-engaged-roster">
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown className="h-4 w-4 text-[#a3823f]" />
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">In CRM · no invoice yet</p>
              <span className="text-[10px] uppercase tracking-[0.24em] text-[#1e3d3a]/40 ml-auto">
                {c.engaged_only || 0} engaged
              </span>
            </div>
            {(data.engaged_roster || []).length === 0 ? (
              <p className="text-[13px] text-[#1e3d3a]/45 py-6 text-center">Everyone in the CRM has been billed.</p>
            ) : (
              <ul className="space-y-1">
                {data.engaged_roster.map((r) => (
                  <li key={r.client_id} data-testid={`fb-engaged-${r.client_id}`} className="py-1.5 flex items-baseline justify-between gap-2 border-b border-[#1e3d3a]/6 last:border-b-0">
                    <div className="min-w-0">
                      <p className="text-[12px] text-[#1e3d3a] truncate">{r.business_name || r.client_id}</p>
                      <p className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45 truncate">
                        {r.assigned_founder ? `Assigned · ${r.assigned_founder}` : r.client_id}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <FounderBankCard onChanged={() => {
        load();
        refreshBankMeta();
      }} />

      <BankReconciliationCard />

      {showPayModal && (
        <PayStaffModal
          sourceBank={myBank}
          onClose={() => setShowPayModal(false)}
          onRunCreated={load}
        />
      )}
    </div>
  );
}

function ForecastCard({ label, value, sub, tone, testid }) {
  const toneCls = tone === "gold"
    ? "border-[#c9a961] bg-[#c9a961]/10"
    : tone === "teal"
    ? "border-[#1e3d3a]/20 bg-[#1e3d3a]"
    : "border-[#1e3d3a]/12 bg-[#eae7de]/60";
  return (
    <div data-testid={testid} className={`rounded-xl border p-4 ${toneCls}`}>
      <p className={`text-[10px] uppercase tracking-[0.22em] ${tone === "teal" ? "text-white/60" : "text-[#a3823f]"}`}>{label}</p>
      <p className={`font-head text-3xl mt-1 tabular-nums ${tone === "teal" ? "text-[#c9a961]" : "text-[#1e3d3a]"}`}>
        {money(value)}
      </p>
      <p className={`text-[10px] uppercase tracking-[0.18em] mt-1 ${tone === "teal" ? "text-white/40" : "text-[#1e3d3a]/45"}`}>{sub}</p>
    </div>
  );
}

function HeroStat({ label, value, accent, testid }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-[0.24em] text-white/45">{label}</p>
      <p
        data-testid={testid}
        className={`text-2xl font-head mt-1 tabular-nums ${accent === "gold" ? "text-[#c9a961]" : "text-white"}`}
      >
        {value}
      </p>
    </div>
  );
}

// ---- Helpers ----------------------------------------------------------------
const money = (v) => Number(v || 0).toLocaleString(undefined, {
  style: "currency", currency: "USD", maximumFractionDigits: 0,
});
const shortMoney = (v) => {
  const n = Number(v || 0);
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
};
